import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  createBroadcastMessage,
  sendBroadcastMessage,
  updateBroadcastMessage,
  deleteBroadcastMessage,
  retry,
  sendTest,
  cancelScheduledBroadcast,
  sendScheduledNow,
  rescheduleBroadcast,
  updateDraftDate,
} from './data';
import { useNotification } from '../../../shared/Notification/useNotification';
import { get } from '../../../lib/api';
import { broadcastMessageKey } from './useFetchBroadcastMessages';

export const useCreateBroadcastMessageMutation = () => {
  const queryClient = useQueryClient();
  const { showError } = useNotification();

  const { mutate: createBroadcastMessageMutation, isLoading } = useMutation(
    createBroadcastMessage,
    {
      onSuccess: ({ data }) => {
        queryClient.setQueryData(
          broadcastMessageKey(data.broadcastMessage.uuid),
          data,
        );
      },

      onError: error => {
        const { errors } = error.response.data;

        showError({
          content: errors[0],
        });
      },
    },
  );

  return { createBroadcastMessageMutation, isLoading };
};

export const useUpdateBroadcastMessageMutation = () => {
  const queryClient = useQueryClient();
  const { showError } = useNotification();

  const { mutate: updateBroadcastMessageMutation, isLoading } = useMutation(
    updateBroadcastMessage,
    {
      onSuccess: ({ data }) => {
        queryClient.setQueryData(
          broadcastMessageKey(data.broadcastMessage.uuid),
          data,
        );
      },
      onError: error => {
        const { errors } = error.response.data;

        showError({
          content: errors[0],
        });
      },
    },
  );

  return { updateBroadcastMessageMutation, isLoading };
};

export const useDeleteBroadcastMessageMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const history = useHistory();
  const { showSuccess } = useNotification();

  const { mutate: deleteBroadcastMessageMutation, isLoading } = useMutation(
    deleteBroadcastMessage,
    {
      onSuccess: (_response, { id }) => {
        // Nothing may fetch the deleted broadcast again from the cache.
        queryClient.removeQueries({ queryKey: broadcastMessageKey(id) });
        queryClient.removeQueries({ queryKey: ['broadcastRecipients', id] });
        queryClient.removeQueries({ queryKey: ['broadcastRecipientRows', id] });

        showSuccess({
          content: t('successDelete'),
        });

        history.push('/');
      },
    },
  );

  return { deleteBroadcastMessageMutation, isLoading };
};

export const useTestBroadcastMessageMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showSuccess, showError } = useNotification();

  const { mutate: testBroadcastMessageMutation } = useMutation(sendTest, {
    // A 422 here is meaningful (no phone number on the tester's account, or
    // the previewed recipient has no value and would be skipped), so show it.
    onError: error => {
      showError({
        content: error?.response?.data?.errors || t('sendTest.error'),
      });
    },
    onSuccess: (_response, { id, previewName }) => {
      // Rendering a test as a specific recipient doesn't change server-side
      // counts, but keep the refresh-into-cache pattern used elsewhere here:
      // a refetch would flip isFetching and unmount the current step.
      get(`/api/client_admin/broadcast_messages/${id}`).then(data =>
        queryClient.setQueryData(broadcastMessageKey(id), data),
      );
      showSuccess({
        content: previewName
          ? t('sendTest.successAs', { name: previewName })
          : t('sendTest.success'),
      });
    },
  });

  return { testBroadcastMessageMutation };
};

export const useRetryMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showSuccess } = useNotification();

  const { mutate: retryMutation, isLoading } = useMutation(retry, {
    onSuccess: ({ data }) => {
      queryClient.setQueryData(
        broadcastMessageKey(data.broadcastMessage.uuid),
        data,
      );
      showSuccess({
        content: t('broadcastSent'),
      });
    },
    onError: (_error, { id }) => {
      queryClient.setQueryData(broadcastMessageKey(id), data => {
        return {
          broadcastMessage: {
            ...data.broadcastMessage,
          },
        };
      });
    },
  });

  return { retryMutation, isLoading };
};

export const useSendBroadcastMessageMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showSuccess } = useNotification();

  const {
    mutate: sendBroadcastMessageMutation,
    isLoading: isSending,
  } = useMutation(sendBroadcastMessage, {
    onSuccess: ({ data }) => {
      const { broadcastMessage } = data;
      const isScheduled = broadcastMessage.status === 'scheduled';

      showSuccess({
        content: isScheduled
          ? t('scheduling.scheduledSuccess')
          : t('broadcastSent'),
      });

      queryClient.setQueryData(broadcastMessageKey(broadcastMessage.uuid), {
        broadcastMessage,
      });
    },
  });

  return { sendBroadcastMessageMutation, isSending };
};

export const useCancelScheduledMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showSuccess } = useNotification();
  const history = useHistory();

  const {
    mutate: cancelScheduledMutation,
    isLoading: isCancelling,
  } = useMutation(cancelScheduledBroadcast, {
    onSuccess: ({ data }) => {
      showSuccess({
        content: t('scheduling.cancelSuccess'),
      });

      const { broadcastMessage } = data;
      queryClient.setQueryData(broadcastMessageKey(broadcastMessage.uuid), {
        broadcastMessage,
      });
      history.push(`/edit/review/${broadcastMessage.uuid}`);
    },
  });

  return { cancelScheduledMutation, isCancelling };
};

export const useSendScheduledNowMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showSuccess } = useNotification();

  const {
    mutate: sendScheduledNowMutation,
    isLoading: isSendingNow,
  } = useMutation(sendScheduledNow, {
    onSuccess: ({ data }) => {
      showSuccess({
        content: t('scheduling.sendNowSuccess'),
      });

      const { broadcastMessage } = data;
      queryClient.setQueryData(broadcastMessageKey(broadcastMessage.uuid), {
        broadcastMessage,
      });
    },
  });

  return { sendScheduledNowMutation, isSendingNow };
};

export const useUpdateDraftDateMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showSuccess } = useNotification();

  const {
    mutate: updateDraftDateMutation,
    isLoading: isUpdatingDraftDate,
  } = useMutation(updateDraftDate, {
    onSuccess: ({ data }) => {
      showSuccess({
        content: t('singleBroadcast.changeDateSuccess'),
      });

      const { broadcastMessage } = data;
      queryClient.invalidateQueries(['campaigns-by-year']);
      queryClient.invalidateQueries(['broadcasts-by-year']);
      queryClient.setQueryData(broadcastMessageKey(broadcastMessage.uuid), {
        broadcastMessage,
      });
    },
  });

  return { updateDraftDateMutation, isUpdatingDraftDate };
};

export const useRescheduleMutation = ({ onSuccessCallback } = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showSuccess } = useNotification();

  const { mutate: rescheduleMutation, isLoading: isRescheduling } = useMutation(
    rescheduleBroadcast,
    {
      onSuccess: ({ data }) => {
        showSuccess({
          content: t('scheduling.rescheduleSuccess'),
        });

        const { broadcastMessage } = data;
        queryClient.invalidateQueries(['broadcastMessages']);
        queryClient.setQueryData(broadcastMessageKey(broadcastMessage.uuid), {
          broadcastMessage,
        });

        if (onSuccessCallback) {
          onSuccessCallback(broadcastMessage);
        }
      },
    },
  );

  return { rescheduleMutation, isRescheduling };
};
